import React, {
	useState,
	useMemo,
	useCallback,
	type ReactNode,
	useRef,
	type FocusEvent,
} from 'react';
import debounce from 'lodash/debounce';
import { ErrorMessage } from '@atlaskit/form';

import { CreatableSelect } from '@atlaskit/select';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type {
	Option,
	Options,
	FormatOptionLabelMeta,
} from '@atlassian/jira-issue-field-select-base/src/common/types.tsx';
import {
	FailedFetchOptionWrapper,
	OptionWrapper,
	ImgSpan,
	FlexSpan,
	ErrorMessageWrapper,
} from '@atlassian/jira-issue-field-select-base/src/ui/failed-fetch-options/index.tsx';
import { filterOptionByLabelAndFilterValues } from '@atlassian/jira-issue-field-select-base/src/ui/filter-options-by-label-and-filter-values/index.tsx';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const FETCH_DEBOUNCE = 300;

export const SearchableSelect = <T,>(props: Props<T>) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<Options | undefined>();
	const [defaultSuggestions, setDefaultSuggestions] = useState<Options | undefined>();
	const [hasLastFetchFailed, setHasLastFetchFailed] = useState<boolean>(false);
	const lastQuery = useRef<string>('');

	const { formatMessage } = useIntl();

	const {
		fieldId,
		isMulti,
		isInvalid,
		placeholder,
		value,
		options,
		components,
		canCreateNewItem,
		classNamePrefix,
		onChangeQueryValue,
		onChange,
		onFocus,
		onBlur,
		fetchSuggestionsOnMount,
		getSuggestions,
		formatOptionLabel,
		noOptionsMessage,
		loadingMessage,
		componentsProps,
		getTransformedSuggestions,
		errorMessagePosition,
		invalidMessage,
		onPostTransformSuggestions,
		formatCreateLabel,
		autoFocus = false,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		isDisabled,
		ariaLabel,
	} = props;

	/** Fetching and transforming results logic below * */
	const handleSuggestions = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'query' implicitly has an 'any' type.
		(results, query) => {
			if (lastQuery.current === query) {
				const response = getTransformedSuggestions(results, formatMessage, query);
				if (onPostTransformSuggestions) {
					onPostTransformSuggestions(response, query);
				}
				setSuggestions(response);
				if (query === '') {
					setDefaultSuggestions(response);
				}
			}
		},
		[formatMessage, getTransformedSuggestions, onPostTransformSuggestions],
	);

	const getFailedFetchMessage = useCallback(
		() => (
			<FailedFetchOptionWrapper>{formatMessage(messages.failedFetch)}</FailedFetchOptionWrapper>
		),
		[formatMessage],
	);

	const requestSuggestions = useCallback(
		async (query = ''): Promise<void> => {
			try {
				const response = await getSuggestions(query, componentsProps);
				setIsLoading(false);
				handleSuggestions(response, query);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setIsLoading(false);
				setHasLastFetchFailed(true);
				const failedOption: Option[] = [
					{
						label: getFailedFetchMessage(),
						value: '',
						isDisabled: true,
					},
				];
				setSuggestions(failedOption);
			}
		},
		[componentsProps, getFailedFetchMessage, getSuggestions, handleSuggestions],
	);

	useMemo(() => {
		if (fetchSuggestionsOnMount) {
			if (!options && !defaultSuggestions) {
				setIsLoading(true);
				requestSuggestions();
			} else if (options) {
				setDefaultSuggestions(options);
				setSuggestions(options);
			} else {
				lastQuery.current = '';
				setIsLoading(true);
				requestSuggestions();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [componentsProps]);

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedRequestSuggestions = useCallback(debounce(requestSuggestions, FETCH_DEBOUNCE), []);

	/** Action handlers below * */
	const onQueryChange = useCallback(
		(query: string): void => {
			debouncedRequestSuggestions.cancel();
			setHasLastFetchFailed(false);
			if (query) {
				const transformedQuery = onChangeQueryValue ? onChangeQueryValue(query) : query;
				lastQuery.current = transformedQuery;
				setIsLoading(true);
				debouncedRequestSuggestions(transformedQuery);
			} else {
				lastQuery.current = '';
				setIsLoading(false);
				setSuggestions(defaultSuggestions);
			}
		},
		[debouncedRequestSuggestions, defaultSuggestions, onChangeQueryValue],
	);

	const onSelectFocus = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			setMenuIsOpen(true);
			if (lastQuery.current === '' && !defaultSuggestions) {
				setIsLoading(true);
				requestSuggestions();
			}
			onFocus && onFocus(e);
		},
		[defaultSuggestions, onFocus, requestSuggestions],
	);

	const onSelectBlur = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			setMenuIsOpen(false);
			onBlur && onBlur(e);
		},
		[onBlur],
	);

	const onChangeSelected = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'values' implicitly has an 'any' type.
		(values) => {
			onChange(values, suggestions);
		},
		[onChange, suggestions],
	);

	/** Custom rendering through functions below * */
	const getFormatOptionLabel = useCallback(
		(option: Option, meta: FormatOptionLabelMeta<Option>) => {
			if (formatOptionLabel !== undefined) {
				return formatOptionLabel(option, meta);
			}

			if (option.iconUrl === undefined) {
				return option.label;
			}

			return (
				<OptionWrapper>
					<ImgSpan>
						<img
							src={option.iconUrl}
							alt=""
							width={2 * gridSize}
							height={2 * gridSize}
							role="none"
						/>
					</ImgSpan>
					<FlexSpan>{option.label}</FlexSpan>
				</OptionWrapper>
			);
		},
		[formatOptionLabel],
	);

	const renderErrorMessage = useCallback(
		() =>
			invalidMessage !== undefined ? (
				<ErrorMessageWrapper
					key={`${fieldId}-error-message`}
					errorMessagePosition={errorMessagePosition}
				>
					<ErrorMessage>{invalidMessage}</ErrorMessage>
				</ErrorMessageWrapper>
			) : null,
		[errorMessagePosition, fieldId, invalidMessage],
	);

	const getCreateLabel = useCallback(
		(inputValue: string): string =>
			`${inputValue} (${
				formatCreateLabel !== undefined ? formatCreateLabel : formatMessage(messages.createNewItem)
			})`,
		[formatCreateLabel, formatMessage],
	);

	const getNoOptionsMessage = useCallback(
		(): string =>
			noOptionsMessage !== undefined ? noOptionsMessage : formatMessage(messages.empty),
		[formatMessage, noOptionsMessage],
	);
	const getLoadingMessage = useCallback(
		(): string => (loadingMessage !== undefined ? loadingMessage : formatMessage(messages.loading)),
		[formatMessage, loadingMessage],
	);

	const filterOption = useCallback(
		(option: Option): boolean => {
			if (hasLastFetchFailed) {
				// If the fetch has failed, we will show a disabled option to indicate the error to users
				return true;
			}
			return filterOptionByLabelAndFilterValues(option, lastQuery.current || '');
		},
		[hasLastFetchFailed],
	);

	const isValidNewOption = useCallback(
		(query: string) => {
			if (canCreateNewItem && query !== '') {
				return true;
			}
			return false;
		},
		[canCreateNewItem],
	);

	/**  Rendering logic below * */
	const renderSelectField = () => (
		<CreatableSelect
			classNamePrefix={classNamePrefix}
			key={fieldId}
			options={suggestions}
			autoFocus={autoFocus}
			// @ts-expect-error TS2345: Argument of type '{ new <Option = OptionType, IsMulti extends boolean = false>(props: SelectProps<Option, IsMulti>): { components: Partial<SelectComponents<Option, IsMulti, GroupBase<Option>>>; ... 23 more ...; UNSAFE_componentWillUpdate?(nextProps: Readonly<...>, nextState: Readonly<...>, nextContext: any): void; }; defaultProps: { ...' is not assignable to parameter of type 'ComponentType<SelectProps<unknown, boolean> | AsyncSelectProps<unknown, boolean> | CreatableSelectProps<unknown, boolean>>'.
			value={value}
			onBlur={onSelectBlur}
			onFocus={onSelectFocus}
			noOptionsMessage={getNoOptionsMessage}
			loadingMessage={getLoadingMessage}
			menuPosition={isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined}
			styles={defaultSelectStyles}
			placeholder={placeholder !== undefined ? placeholder : formatMessage(messages.placeholder)}
			onInputChange={onQueryChange}
			onChange={onChangeSelected}
			isLoading={menuIsOpen && isLoading}
			filterOption={filterOption}
			isClearable
			formatOptionLabel={getFormatOptionLabel}
			// @ts-expect-error - Type '{ Menu: ComponentType<MenuProps>; } | undefined' is not assignable to type 'Partial<SelectComponents<Option, boolean, GroupBase<Option>>> | undefined'
			components={components}
			componentsProps={componentsProps}
			isMulti={isMulti}
			isValidNewOption={isValidNewOption}
			// @ts-expect-error - null has to be changed to undefined
			validationState={isInvalid === true ? 'error' : null}
			formatCreateLabel={getCreateLabel}
			closeMenuOnScroll={onCloseMenuOnScroll}
			isDisabled={isDisabled}
			aria-label={ariaLabel}
			{...(expVal(
				'enhanced_multiselect_linking_experiment',
				'isEnhanceMultiSelectLinkedIssueEnable',
				false,
			)
				? { closeMenuOnSelect: false }
				: {})}
		/>
	);
	const componentList: ReactNode[] = [renderSelectField()];
	errorMessagePosition === 'top'
		? componentList.unshift(renderErrorMessage())
		: componentList.push(renderErrorMessage());

	return <>{componentList}</>;
};

SearchableSelect.defaultProps = {
	isMulti: false,
	canCreateNewItem: false,
	fetchSuggestionsOnMount: true,
};

export default SearchableSelect;
